import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { ApiError } from '@iptv/api-client';

/** Localized message for any thrown value; API errors are localized by their stable code. */
export function useErrorText(): (err: unknown) => string {
  const { t } = useTranslation();
  return useCallback(
    (err: unknown) => {
      if (err instanceof ApiError)
        return t(`errors.${err.code}`, { defaultValue: t('errors.INTERNAL') });
      return t('errors.INTERNAL');
    },
    [t],
  );
}

/**
 * Maps server-side validation details (path "a.b.c") onto form fields. Returns true when at least one
 * field received an error, so callers can skip a generic toast.
 */
export function applyServerErrors<T extends FieldValues>(
  err: unknown,
  setError: UseFormSetError<T>,
  fields: readonly string[],
  replacementMessage?: string,
): boolean {
  if (!(err instanceof ApiError) || err.code !== 'VALIDATION_FAILED' || !err.details) return false;
  let applied = false;
  for (const d of err.details) {
    const path = d.path.replace(/^body\./, '');
    if (fields.some((f) => path === f || path.startsWith(`${f}.`))) {
      setError(path as Path<T>, { type: 'server', message: replacementMessage ?? d.message });
      applied = true;
    }
  }
  return applied;
}

export function isApiError(err: unknown, code?: ApiError['code']): err is ApiError {
  return err instanceof ApiError && (code === undefined || err.code === code);
}

/**
 * Hook form of applyServerErrors. Server validation messages are English, so in other languages the
 * field is flagged with a localized generic message instead.
 */
export function useServerErrors() {
  const { t, i18n } = useTranslation();
  return useCallback(
    <T extends FieldValues>(
      err: unknown,
      setError: UseFormSetError<T>,
      fields: readonly string[],
    ) =>
      applyServerErrors(
        err,
        setError,
        fields,
        i18n.language === 'en' ? undefined : t('validation.invalid'),
      ),
    [t, i18n.language],
  );
}
